import React from "react";
import "../../Assets/Styles/onboarding-v2.css";

/**
 * ONBOARDING KIT — the single source of the v2 onboarding design.
 *
 * Three flows share this look (aptitude/OnboardingModal, career/CountryModal,
 * IELTS/IELTSOnboardingModal). Before this kit each carried its own copy of
 * the old chrome, which is exactly how they drifted apart; now a step change
 * here restyles all of them at once. The kit owns MARKUP AND STYLE ONLY —
 * every flow keeps its own state machine, handlers and completion semantics.
 */

/** 🇰🇼-style emoji flag from an ISO alpha-2 code */
export const flagOf = (a2) =>
  (a2 || "").toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

/** Full-screen shell: ambient blobs + top chrome (back · progress/step · close). */
export const OnbShell = ({ arabic = true, stepIdx, stepTotal, progress, showBack, onBack, onClose, children }) => (
  <div className="onb2" dir={arabic ? "rtl" : "ltr"}>
    <div className="onb2-blob onb2-blob--a" aria-hidden="true" />
    <div className="onb2-blob onb2-blob--b" aria-hidden="true" />
    <header className="onb2-top">
      {showBack ? (
        <button type="button" className="onb2-ctrl" aria-label={arabic ? "السابق" : "Back"} onClick={onBack}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: arabic ? "scaleX(-1)" : "none" }}><path d="m15 18-6-6 6-6" /></svg>
        </button>
      ) : (
        <span className="onb2-ctrl onb2-ctrl--ghost" aria-hidden="true" />
      )}
      <div className="onb2-progress-wrap">
        <div className="onb2-progress"><span style={{ width: `${progress}%` }} /></div>
        <span className="onb2-stepnum">{stepIdx} / {stepTotal}</span>
      </div>
      <button type="button" className="onb2-ctrl" aria-label={arabic ? "إغلاق" : "Close"} onClick={onClose}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
      </button>
    </header>
    <main className="onb2-stage">{children}</main>
  </div>
);

/** One step: icon chip, question, optional hint, then whatever the step needs.
    Key it on the page id so the enter animation replays per step. */
export const OnbStep = ({ navDir, icon, title, hint, children }) => (
  <section className={`onb2-step${navDir === "back" ? " onb2-step--back" : ""}`}>
    {icon ? <div className="onb2-icon" aria-hidden="true">{icon}</div> : null}
    <h2 className="onb2-q" id="onb2-question">{title}</h2>
    {hint ? <p className="onb2-hint">{hint}</p> : null}
    {children}
  </section>
);

const Check = () => (
  <span className="onb2-opt-check" aria-hidden="true">
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
  </span>
);

/** Option cards. options: [{ key, icon?, label, onPick }]. `row` renders the
    compact horizontal card (used for country lists and long labels). */
export const OnbOptions = ({ options, selectedKey, cols, row = false, delayBase = 120 }) => (
  <div className={`onb2-grid${cols ? ` onb2-grid--${cols}` : options.length === 3 ? " onb2-grid--3" : ""}`}>
    {options.map((o, i) => (
      <button key={o.key} type="button"
        className={`onb2-opt${row ? " onb2-opt--row" : ""}${selectedKey === o.key ? " onb2-opt--on" : ""}`}
        style={{ animationDelay: `${delayBase + i * 60}ms` }}
        onClick={o.onPick}>
        {o.icon ? <span className="onb2-opt-icon" aria-hidden="true">{o.icon}</span> : null}
        <span className="onb2-opt-label">{o.label}</span>
        <Check />
      </button>
    ))}
  </div>
);

/**
 * autoFocus scrolls the focused input into view, and while the step is still
 * sliding in (translateX) the browser scrolls the modal sideways to "reveal"
 * it — leaving the whole screen shifted until the next step change. Focusing
 * with preventScroll keeps the caret without moving anything.
 */
const useSoftAutoFocus = () => {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const id = window.requestAnimationFrame(() => {
      try {
        el.focus({ preventScroll: true });
      } catch {
        el.focus();
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, []);
  return ref;
};

/** Themed free-text answer with optional "not sure" escape and continue CTA. */
export const OnbTextStep = ({ value, onChange, placeholder, canContinue, onContinue, continueLabel, ghostLabel, onGhost, inputMode }) => {
  const focusRef = useSoftAutoFocus();
  return (
  <div className="onb2-field">
    <input className="onb2-search onb2-field-input" ref={focusRef} placeholder={placeholder}
      value={value} inputMode={inputMode} onChange={onChange} autoComplete="off"
      onKeyDown={(e) => { if (e.key === "Enter" && canContinue) onContinue(); }} />
    {canContinue ? (
      <button type="button" className="onb2-continue" onClick={onContinue}>{continueLabel}</button>
    ) : onGhost ? (
      <button type="button" className="onb2-ghost" onClick={onGhost}>{ghostLabel}</button>
    ) : null}
  </div>
  );
};

/** Country picker: themed search + results dropdown + recommended flag cards. */
export const OnbCountryPicker = ({ arabic = true, searchText, onSearchInput, countries, loading, recommended, selectedId, onPick, hint }) => {
  const focusRef = useSoftAutoFocus();
  return (
  <div className="onb2-country">
    <div className="onb2-search-wrap">
      <svg className="onb2-search-ic" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
      <input className="onb2-search" ref={focusRef}
        placeholder={arabic ? "ابحث عن جنسيتك…" : "Search your nationality…"}
        value={searchText} onChange={onSearchInput} autoComplete="off" />
      {loading ? <span className="onb2-search-spin" aria-hidden="true" /> : null}
      {searchText && countries?.length ? (
        <div className="onb2-search-panel">
          {countries.map((c) => (
            <button key={c.id || c.english_name} type="button" className="onb2-search-item" onClick={() => onPick(c)}>
              <span className="onb2-search-flag" aria-hidden="true">{flagOf(c.alpha2_code)}</span>
              <span>{arabic ? c.arabic_name || c.english_name : c.english_name}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
    <div className="onb2-country-hint">{hint || (arabic ? "أو اختر من الجنسيات الشائعة:" : "Or pick a common nationality:")}</div>
    <div className="onb2-grid onb2-grid--country">
      {recommended.map((c, i) => (
        <button key={c.id} type="button"
          className={`onb2-opt onb2-opt--row${selectedId === c.id ? " onb2-opt--on" : ""}`}
          style={{ animationDelay: `${120 + i * 60}ms` }}
          onClick={() => onPick(c)}>
          <span className="onb2-opt-icon" aria-hidden="true">{flagOf(c.alpha2_code)}</span>
          <span className="onb2-opt-label">{arabic ? c.arabic_name : c.english_name}</span>
          <Check />
        </button>
      ))}
    </div>
  </div>
  );
};

/** A smaller follow-up question inside a step (branching sub-sections). */
export const OnbSubQuestion = ({ children }) => <h3 className="onb2-subq">{children}</h3>;
